import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Send, CheckCircle, Flame } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL || "";

const WishPage = () => {
  const [productName, setProductName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productName.trim()) {
      toast.error("Bitte Produktname eingeben");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/wishes`, {
        product_name: productName.trim(),
        comment: comment.trim()
      });
      
      toast.success(res.data.message);
      setSubmitted(true);
      setProductName("");
      setComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4 overflow-auto" style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Flame className="w-8 h-8 text-rose-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              Produktwunsch
            </CardTitle>
            <p className="text-slate-500 text-sm mt-2">
              Feuerwehr Lüneburg Mitte - Snackautomat
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Danke für deinen Wunsch!
                </h3>
                <p className="text-slate-500 mb-6">
                  Wir prüfen deinen Vorschlag und melden uns ggf. zurück.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="mt-4"
                >
                  Weiteren Wunsch einreichen
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="product">Welches Produkt wünschst du dir? *</Label>
                  <Input
                    id="product"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="z.B. Snickers, Red Bull, etc."
                    data-testid="wish-product-input"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comment">Anmerkung (optional)</Label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="z.B. Variante, Größe, etc."
                    rows={3}
                    data-testid="wish-comment-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  data-testid="wish-submit-btn"
                >
                  {submitting ? (
                    "Wird gesendet..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Wunsch einreichen
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-slate-400 mt-4">
                  Ähnliche Wünsche werden zusammengefasst und erhalten mehr Stimmen
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default WishPage;
